/**
 * QCDOC application tokens using the supplied Tecton action palette.
 *
 * Primary and hover colours come from Tecton's light and dark variables.
 * Pressed actions reuse hover; subtle surfaces reuse the matching palette.
 * Semantic and chart accents remain separate from the action palette.
 */

export interface ThemeTokens {
  // ── Brand ─────────────────────────────────────────
  colorPrimary: string;
  colorPrimaryHover: string;
  colorPrimaryActive: string;
  colorPrimaryBg: string;
  colorPrimaryForeground: string;
  colorPrimaryHoverForeground: string;

  // ── Navy accent ───────────────────────────────────
  colorNavy: string;
  colorNavyDark: string;
  colorNavyLight: string;

  // ── Backgrounds ───────────────────────────────────
  colorBgLayout: string;
  colorBgContainer: string;
  colorBgElevated: string;
  colorBgSpotlight: string;

  // ── Text ──────────────────────────────────────────
  colorText: string;
  colorTextSecondary: string;
  colorTextTertiary: string;
  colorTextQuaternary: string;

  // ── Borders ───────────────────────────────────────
  colorBorder: string;
  colorBorderSecondary: string;

  // ── Semantic ──────────────────────────────────────
  colorSuccess: string;
  colorSuccessBg: string;
  colorWarning: string;
  colorWarningBg: string;
  colorError: string;
  colorErrorBg: string;
  colorInfo: string;
  colorInfoBg: string;
  colorLink: string;
  colorLinkHover: string;

  // ── Sidebar ───────────────────────────────────────
  sidebarBg: string;
  sidebarBorder: string;
  sidebarTextColor: string;
  sidebarTextMuted: string;
  sidebarHoverBg: string;
  sidebarSelectedBg: string;
  sidebarSelectedColor: string;
  sidebarIndicatorColor: string;
  sidebarLogoBg: string;
  sidebarSectionLabel: string;
  sidebarScrollThumb: string;

  // ── Header ────────────────────────────────────────
  headerBg: string;
  headerBorder: string;
  headerText: string;

  // ── Shadows ───────────────────────────────────────
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
  shadowCard: string;

  // ── Table ─────────────────────────────────────────
  tableHeaderBg: string;
  tableHeaderColor: string;
  tableRowHoverBg: string;
  tableRowSelectedBg: string;
  tableBorder: string;
}

// ── Typography (shared between modes) ─────────────────
export const typography = {
  fontFamily: '"Figtree", "Segoe UI", Arial, sans-serif',
  fontSizeSm: 12,
  fontSizeMd: 14,
  fontSizeLg: 16,
  fontSizeXl: 18,
  fontSizeH1: 28,
  fontSizeH2: 22,
  fontSizeH3: 18,
  fontWeightNormal: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  lineHeight: 1.6,
} as const;

// ── Spacing (shared) ─────────────────────────────────
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
} as const;

// ── Layout (shared) ──────────────────────────────────
export const layout = {
  sidebarWidth: 240,
  sidebarCollapsedWidth: 64,
  headerHeight: 64,
  contentMaxWidth: 1440,
  contentPadding: 24,
  borderRadius: 6,
  borderRadiusSm: 4,
  borderRadiusLg: 8,
} as const;

// ═════════════════════════════════════════════════════════
//  LIGHT THEME
// ═════════════════════════════════════════════════════════
export const lightTokens: ThemeTokens = {
  // Tecton actions
  colorPrimary: '#644a78',
  colorPrimaryHover: '#865fa0',
  colorPrimaryActive: '#865fa0',
  colorPrimaryBg: '#f7f3f8',
  colorPrimaryForeground: '#f7f3f8',
  colorPrimaryHoverForeground: '#ffffff',

  // Navy
  colorNavy: '#003366',
  colorNavyDark: '#002244',
  colorNavyLight: '#004080',

  // Backgrounds
  colorBgLayout: '#F5F5F5',
  colorBgContainer: '#FFFFFF',
  colorBgElevated: '#FFFFFF',
  colorBgSpotlight: '#002244',

  // Text
  colorText: '#333333',
  colorTextSecondary: '#666666',
  colorTextTertiary: '#999999',
  colorTextQuaternary: '#CCCCCC',

  // Borders
  colorBorder: '#E0E0E0',
  colorBorderSecondary: '#F0F0F0',

  // Semantic
  colorSuccess: '#2E7D32',
  colorSuccessBg: '#E8F5E9',
  colorWarning: '#ED6C02',
  colorWarningBg: '#FFF3E0',
  colorError: '#D32F2F',
  colorErrorBg: '#FFEBEE',
  colorInfo: '#0277BD',
  colorInfoBg: '#E1F5FE',
  colorLink: '#644a78',
  colorLinkHover: '#865fa0',

  // Sidebar — light surface
  sidebarBg: '#FFFFFF',
  sidebarBorder: '#F0F0F0',
  sidebarTextColor: 'rgba(0, 0, 0, 0.88)',
  sidebarTextMuted: 'rgba(0, 0, 0, 0.45)',
  sidebarHoverBg: '#f7f3f8',
  sidebarSelectedBg: '#f7f3f8',
  sidebarSelectedColor: '#644a78',
  sidebarIndicatorColor: '#644a78',
  sidebarLogoBg: '#ffffff',
  sidebarSectionLabel: 'rgba(0, 0, 0, 0.55)',
  sidebarScrollThumb: 'rgba(0, 0, 0, 0.12)',

  // Header
  headerBg: '#FFFFFF',
  headerBorder: '#F0F0F0',
  headerText: '#333333',

  // Shadows
  shadowSm: '0 1px 3px rgba(0,0,0,0.08)',
  shadowMd: '0 2px 8px rgba(0,0,0,0.10)',
  shadowLg: '0 4px 16px rgba(0,0,0,0.12)',
  shadowCard: '0 1px 4px rgba(0,51,102,0.06)',

  // Table
  tableHeaderBg: '#FAFAFA',
  tableHeaderColor: '#003366',
  tableRowHoverBg: '#F8FAFB',
  tableRowSelectedBg: '#f7f3f8',
  tableBorder: '#F0F0F0',
};

// ═════════════════════════════════════════════════════════
//  DARK THEME
// ═════════════════════════════════════════════════════════
export const darkTokens: ThemeTokens = {
  // Tecton actions with contrast text for links on dark surfaces.
  colorPrimary: '#5d4d68',
  colorPrimaryHover: '#74647f',
  colorPrimaryActive: '#74647f',
  colorPrimaryBg: '#5d4d68',
  colorPrimaryForeground: '#e5e0eb',
  colorPrimaryHoverForeground: '#ffffff',

  // Navy — lighter for dark backgrounds
  colorNavy: '#4D9FD6',
  colorNavyDark: '#3380B5',
  colorNavyLight: '#66B3E0',

  // Backgrounds
  colorBgLayout: '#0F0F0F',
  colorBgContainer: '#1A1A1A',
  colorBgElevated: '#242424',
  colorBgSpotlight: '#1A1A1A',

  // Text — inverted
  colorText: 'rgba(255, 255, 255, 0.88)',
  colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
  colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
  colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',

  // Borders
  colorBorder: '#333333',
  colorBorderSecondary: '#2A2A2A',

  // Semantic — slightly brighter for dark backgrounds
  colorSuccess: '#4CAF50',
  colorSuccessBg: 'rgba(76, 175, 80, 0.12)',
  colorWarning: '#FFA726',
  colorWarningBg: 'rgba(255, 167, 38, 0.12)',
  colorError: '#EF5350',
  colorErrorBg: 'rgba(239, 83, 80, 0.12)',
  colorInfo: '#29B6F6',
  colorInfoBg: 'rgba(41, 182, 246, 0.12)',
  colorLink: '#e5e0eb',
  colorLinkHover: '#ffffff',

  // Sidebar — dark surface
  sidebarBg: '#141414',
  sidebarBorder: '#2A2A2A',
  sidebarTextColor: 'rgba(255, 255, 255, 0.88)',
  sidebarTextMuted: 'rgba(255, 255, 255, 0.45)',
  sidebarHoverBg: '#5d4d68',
  sidebarSelectedBg: '#5d4d68',
  sidebarSelectedColor: '#e5e0eb',
  sidebarIndicatorColor: '#e5e0eb',
  sidebarLogoBg: '#ffffff',
  sidebarSectionLabel: 'rgba(255, 255, 255, 0.55)',
  sidebarScrollThumb: 'rgba(255, 255, 255, 0.12)',

  // Header
  headerBg: '#141414',
  headerBorder: '#2A2A2A',
  headerText: 'rgba(255, 255, 255, 0.88)',

  // Shadows — subtler on dark backgrounds
  shadowSm: '0 1px 3px rgba(0,0,0,0.30)',
  shadowMd: '0 2px 8px rgba(0,0,0,0.40)',
  shadowLg: '0 4px 16px rgba(0,0,0,0.50)',
  shadowCard: '0 1px 4px rgba(0,0,0,0.25)',

  // Table
  tableHeaderBg: '#1F1F1F',
  tableHeaderColor: '#66B3E0',
  tableRowHoverBg: 'rgba(255, 255, 255, 0.04)',
  tableRowSelectedBg: '#5d4d68',
  tableBorder: '#2A2A2A',
};

/** Get tokens for a given mode */
export function getTokens(mode: 'light' | 'dark'): ThemeTokens {
  return mode === 'dark' ? darkTokens : lightTokens;
}
